import { isPreview } from '~/dev/utils/isPreview';

import { CLIENTLESS_ROUTES } from './constants';

/** Serializes/stringifies a JS object to base64-encoded string */
export const serializeToBase64 = <X>(object: X): string =>
  btoa(JSON.stringify(object));

/** Parses a base64-encoded string to a JS object */
export const parseFromBase64 = <X>(string: string): X =>
  JSON.parse(atob(string));

/*
 * In our app previews the first segment will be preview--<pr-number>--<workspace>/ so we need to parse
 * this out to get the workspace name without dropping the pr-number from the url which we need
 * for uniqueness (otherwise different PRs would overwrite one another).
 *
 * Our app has too many places that assume a certain path structure so I gave up on trying to make
 * /preview/<pr-id>/<workspace>/ work.
 */
export const parseWorkspace = (ws: string) => {
  if (/(.+)--(.+)--(.+)/.test(ws) && isPreview) {
    return ws.split('--')[2];
  }
  return ws;
};

export const shouldSkipSubdomainCheck = (pathname: string): boolean =>
  pathname.includes('/redirects/evisync/') ||
  pathname.includes('folders-permission-check') ||
  pathname.startsWith('/preview/');

export const getRawClientFromUrl = () => {
  const url = window.location.pathname;
  const rawSubdomain = url.split('/')[1];
  const isClientlessRoute = (CLIENTLESS_ROUTES as string[]).includes(
    `/${rawSubdomain}`,
  );

  return isClientlessRoute ? '' : rawSubdomain;
};

export const getClientFromUrl = () => {
  const rawSubdomain = getRawClientFromUrl();

  if (rawSubdomain) {
    return parseWorkspace(rawSubdomain);
  }

  return '';
};

/** Receives a full URL and extracts the application route. */
export const getApplicationRoute = (fullUrl: string): string => {
  const url = new URL(fullUrl);
  const paths = url.pathname.split('/');
  const [_empty, _client, ...appRoutes] = paths;

  return `/${appRoutes.join('/')}`;
};

export const setSearchParams = (
  path: string,
  searchParams: Record<string, string> = {},
): string => {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(searchParams)) {
    url.searchParams.set(key, value);
  }
  return url.toString();
};
